import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Scan, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { scanAPI } from '../lib/api';
import { cn } from '../lib/utils';

const Scanner = ({ isOpen, onClose, onScanComplete }) => {
  const [scanCode, setScanCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const processScan = useCallback(async (code) => {
    if (scanning || !code.trim()) return;
    setScanning(true);
    setError(null);
    setResult(null);

    try {
      const response = await scanAPI.process(code.trim());
      const scanResult = response.data;
      
      setResult(scanResult);
      
      if (scanResult.success) {
        // Play success sound
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
          oscillator.connect(audioContext.destination);
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.1);
        } catch (e) { /* Audio not available */ }
        
        // Vibrate on success
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
      } else {
        // Play error sound
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          oscillator.type = 'square';
          oscillator.frequency.setValueAtTime(220, audioContext.currentTime);
          oscillator.connect(audioContext.destination);
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.2);
        } catch (e) { /* Audio not available */ }
        
        // Vibrate on error
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }
      }
      
      onScanComplete?.(scanResult);
      
      // Clear input and result after delay, refocus for next scan
      setTimeout(() => {
        setResult(null);
        setScanCode('');
        inputRef.current?.focus();
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Scan fehlgeschlagen');
      // Clear input after error
      setTimeout(() => {
        setScanCode('');
        inputRef.current?.focus();
      }, 2000);
    } finally {
      setScanning(false);
    }
  }, [scanning, onScanComplete]);

  // Auto-focus input when scanner opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle keyboard input - auto-scan on Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && scanCode.trim()) {
      e.preventDefault();
      processScan(scanCode);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setScanCode('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="scanner-overlay" data-testid="scanner-overlay">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center">
        <h2 className="text-white text-xl font-semibold heading">Barcode scannen</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-white hover:bg-white/10"
          data-testid="scanner-close-btn"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Scanner Icon */}
      <div className="absolute top-16 left-0 right-0 flex justify-center p-4">
        <div className="bg-emerald-600 rounded-full p-3">
          <Scan className="h-6 w-6 text-white" />
        </div>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md mx-auto px-4">
        <div className="w-full space-y-4">
          {/* Scan Input - auto-focused, triggers on Enter */}
          <div className="text-center mb-4">
            <p className="text-white/80 text-sm mb-2">
              Scanner-Gerät verwenden oder Barcode eingeben
            </p>
          </div>
          <Input
            ref={inputRef}
            type="text"
            placeholder="Barcode hier scannen oder eingeben..."
            value={scanCode}
            onChange={(e) => setScanCode(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-white/10 border-white/30 text-white placeholder:text-white/50 text-center text-xl mono h-14"
            autoFocus
            autoComplete="off"
            data-testid="scanner-input"
          />
          <p className="text-white/50 text-xs text-center">
            Drücken Sie Enter oder scannen Sie direkt mit dem Scanner-Gerät
          </p>
        </div>

        {/* Scanning indicator */}
        {scanning && (
          <div className="mt-6 flex items-center gap-2 text-white">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            <span>Verarbeite...</span>
          </div>
        )}

        {/* Result Display */}
        {result && (
          <div
            className={cn(
              'mt-6 p-4 rounded-lg w-full text-center',
              result.success ? 'bg-emerald-500/20 scan-success' : 'bg-red-500/20 scan-error'
            )}
            data-testid="scanner-result"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              {result.success ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-400" />
              )}
              <span className={cn(
                'text-lg font-semibold',
                result.success ? 'text-emerald-400' : 'text-red-400'
              )}>
                {result.success ? 'Erfolgreich' : 'Fehler'}
              </span>
            </div>
            {result.container_name && (
              <p className="text-white mb-1">{result.container_name}</p>
            )}
            {result.success && result.remaining_cycles !== null && (
              <p className="text-white/70 text-sm">Restzyklen: {result.remaining_cycles}</p>
            )}
            {result.error_message && (
              <p className="text-red-300 text-sm mt-2">{result.error_message}</p>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 rounded-lg bg-red-500/20 text-red-300 text-center" data-testid="scanner-error">
            {error}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-center text-white/60 text-sm">
        USB-/Bluetooth-Scanner senden automatisch Enter nach dem Scan
      </div>
    </div>
  );
};

export default Scanner;
